import { inject, injectable } from 'inversify'
import { Group } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { KeyCodes } from '@/shared/constants'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { TrimEntityCommand } from '../command/TrimEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IPoint } from '../model/common/interface'
import type { IEntity } from '../model/entity'
import { EntityObjectUtils } from '../object/entityObject/entityObjectUtils'
import { createCadLine2Object } from '../object/material/CadLineFactory'
import type { EntityDetector } from '../selector/entityDetector'
import type { EntitySelector } from '../selector/entitySelector'
import { EntityEditHandler } from './EntityEditHandler'
import { TrimUtils } from './utils/trimUtils'

const TRIM_STATUS_MOUSE_TIP_ID = 'TrimStatusMouseTipId'

// ─── 状态机定义 ────────────────────────────────────────────────────────────────
// idle → trimming（悬停预览 + 左键执行修剪，ESC 退出）
const trimEntityMachine = setup({
  actions: {
    entryTrimming: () => {},
  },
}).createMachine({
  id: 'trimEntity',
  initial: 'idle',
  states: {
    idle: {
      on: { NEXT: 'trimming' },
    },
    trimming: {
      entry: { type: 'entryTrimming' },
      // 保持在此状态，通过鼠标事件处理修剪逻辑，ESC 调用 exit()
    },
  },
})

// ─── TrimEntityTool ───────────────────────────────────────────────────────────

/**
 * 修剪编辑工具
 *
 * 交互流程：
 * 1. **trimming**：鼠标移动时自动以场景中所有其他实体为剪切边，
 *    高亮预览将要修剪的部分，左键单击执行修剪；
 *    可继续在其他实体上修剪，ESC 退出
 */
@injectable()
export class TrimEntityTool
  extends StateMachineTool<typeof trimEntityMachine>
  implements MouseEventInfoHandler, KeyEventInfoHandler
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.TrimEntityTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.EntityDetector)
  private readonly entityDetector!: EntityDetector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  protected machine = trimEntityMachine.provide({
    actions: {
      entryTrimming: () => this.entryTrimming(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController

  /** 当前光标世界坐标 */
  private cursorPos: IPoint = { x: 0, y: 0 }

  /** 预览相关 */
  private group: Group = new Group()
  private keptPreviewObjects: Line2[] = []
  private trimmedPreviewObjects: Line2[] = []
  /** 当前预览实体（光标悬停的实体）及其原始显示状态 */
  private hoveredEntity?: IEntity
  private hoveredEntityObject?: Line2
  private hoveredEntityOriginalMaterial?: Line2['material']
  private hoveredEntityPreviewMaskMaterial?: Line2['material']

  private currentCommand?: TrimEntityCommand
  private trimResultCache?: ReturnType<typeof EntityEditHandler.trim>

  // ─── 生命周期 ─────────────────────────────────────────────────────────────

  protected onInitialize(): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.currentCommand = new TrimEntityCommand(this.viewManager)
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.entityDetector.resetConfig()
    this.clearPreviewObjects()
    this.restoreHoveredEntity()
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Edit)
    this.hideTrimStatusTip()
    this.eject(this.entityDetector)
    this.hoveredEntity = undefined
    this.hoveredEntityObject = undefined
    this.hoveredEntityOriginalMaterial = undefined
    this.hoveredEntityPreviewMaskMaterial = undefined
    this.currentCommand = undefined
    this.trimResultCache = undefined
  }

  // ─── 鼠标 / 键盘事件 ──────────────────────────────────────────────────────

  /**
   * 鼠标按下：左键在修剪阶段提交修剪操作。
   *
   * @param params - 鼠标事件参数
   */
  onMouseDown(params: MouseEventParams): void {
    if (params.event.button === 0 && this.checkState('trimming')) {
      this.executeOperate()
    }
  }

  /**
   * 鼠标移动：更新光标位置并刷新修剪预览。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams): void {
    this.cursorPos = { x: params.worldPos.x, y: params.worldPos.y }
    if (this.checkState('trimming')) {
      this.updatePreview()
    }
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams): void {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  // ─── 状态机动作 ────────────────────────────────────────────────────────────

  private entryTrimming(): void {
    this.entityDetector.config({ enableDetect: true, enableHighlight: false })
    this.inject(this.entityDetector)
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.trim.target)
  }

  // ─── 边界收集 ──────────────────────────────────────────────────────────────

  /**
   * 以场景中除目标实体外的所有实体作为剪切边（与延伸工具同理，无需手动选择）
   */
  private getBoundaries(exclude: IEntity): IEntity[] {
    return this.viewManager.getVisibleEntities().filter((e) => e !== exclude)
  }

  // ─── 预览更新 ──────────────────────────────────────────────────────────────

  /**
   * 在 trimming 状态下每次鼠标移动时更新预览：
   * - 探测光标下方的实体
   * - 计算修剪结果
   * - 隐藏原实体，在 temp group 中分别显示保留片段与将被修剪的片段
   */
  private updatePreview(): void {
    try {
      this.updatePreviewInternal()
    } catch (error) {
      this.restoreHoveredEntity()
      this.clearPreviewObjects()
      throw error
    }
  }

  private updatePreviewInternal(): void {
    this.trimResultCache = undefined // 鼠标移动时清除缓存，重新计算修剪结果
    const entity = this.entityDetector.getOneEntity()

    if (entity !== this.hoveredEntity) {
      this.restoreHoveredEntity()
      this.clearPreviewObjects()
    }

    if (!entity) {
      this.hideTrimStatusTip()
      return
    }

    const boundaries = this.getBoundaries(entity)
    const trimResult = EntityEditHandler.trim(entity, {
      cursorPoint: this.cursorPos,
      boundaries,
    })
    this.trimResultCache = trimResult

    if (trimResult.deleteEntities.length === 0) {
      this.restoreHoveredEntity()
      this.clearPreviewObjects()
      this.showDisabledTip()
      return
    }

    this.showTrimTip()

    // 首次悬停：记录实体对象并用透明材质遮掉原显示，由预览对象接管显示。
    if (entity !== this.hoveredEntity) {
      this.hoveredEntity = entity
      this.viewManager.objectManager.beginEntityEdit?.(entity)
      this.hoveredEntityObject = this.viewManager.getEntityObject(entity) as Line2
      this.hoveredEntityOriginalMaterial = this.hoveredEntityObject?.material
      if (this.hoveredEntityObject) {
        this.hoveredEntityPreviewMaskMaterial = this.createPreviewMaskMaterial(
          this.hoveredEntityOriginalMaterial
        )
        if (this.hoveredEntityPreviewMaskMaterial) {
          this.hoveredEntityObject.material = this.hoveredEntityPreviewMaskMaterial
        }
      }
    }

    const trimPreviewEntities = TrimUtils.getTrimmedPreviewEntities(
      entity,
      this.cursorPos,
      boundaries
    )
    const lockedMaterial = this.viewManager.objectManager
      .getMaterialManager()
      .getCachedLockedMaterial(entity)

    // 原实体被隐藏后，保留段和待修剪段都由临时对象绘制，避免半透明灰叠加原色。
    this.updatePreviewObjects(
      this.keptPreviewObjects,
      trimResult.createdEntities,
      this.hoveredEntityOriginalMaterial
    )
    this.updatePreviewObjects(this.trimmedPreviewObjects, trimPreviewEntities, lockedMaterial)
  }

  private updatePreviewObjects(
    objects: Line2[],
    entities: IEntity[],
    material: Line2['material'] | undefined
  ): void {
    if (!material) {
      objects.forEach((o) => (o.visible = false))
      return
    }

    entities.forEach((previewEntity, i) => {
      let obj = objects[i]
      const geometry = EntityObjectUtils.createEntityLineGeometry(previewEntity)
      if (!geometry) {
        if (obj) obj.visible = false
        return
      }
      if (!obj) {
        obj = createCadLine2Object(geometry, material)
        obj.computeLineDistances()
        this.group.add(obj)
        objects[i] = obj
      } else {
        obj.geometry.dispose()
        obj.geometry = geometry
        obj.material = material
        obj.computeLineDistances()
        obj.visible = true
      }
    })
    objects.slice(entities.length).forEach((o) => (o.visible = false))
  }

  private createPreviewMaskMaterial(
    material: Line2['material'] | undefined
  ): Line2['material'] | undefined {
    if (!material || typeof material.clone !== 'function') {
      return material
    }

    const maskMaterial = material.clone()
    maskMaterial.transparent = true
    maskMaterial.opacity = 0
    maskMaterial.depthWrite = false
    return maskMaterial
  }

  /** 还原当前悬停实体的原始显示状态 */
  private restoreHoveredEntity(): void {
    if (this.hoveredEntityObject && this.hoveredEntityOriginalMaterial) {
      this.hoveredEntityObject.material = this.hoveredEntityOriginalMaterial
    }
    if (
      this.hoveredEntityPreviewMaskMaterial &&
      this.hoveredEntityPreviewMaskMaterial !== this.hoveredEntityOriginalMaterial
    ) {
      this.hoveredEntityPreviewMaskMaterial.dispose()
    }
    if (this.hoveredEntity) this.viewManager.objectManager.endEntityEdit?.(this.hoveredEntity)
    this.hoveredEntity = undefined
    this.hoveredEntityObject = undefined
    this.hoveredEntityOriginalMaterial = undefined
    this.hoveredEntityPreviewMaskMaterial = undefined
  }

  /** 销毁并清空所有预览 Line2 对象 */
  private clearPreviewObjects(): void {
    ;[...this.keptPreviewObjects, ...this.trimmedPreviewObjects].forEach((obj) => {
      obj.geometry.dispose()
      this.group.remove(obj)
    })
    this.keptPreviewObjects = []
    this.trimmedPreviewObjects = []
  }

  private showTrimTip(): void {
    this.mouseTipHelper.addIconTip(TRIM_STATUS_MOUSE_TIP_ID, 'x')
  }

  private showDisabledTip(): void {
    this.mouseTipHelper.addIconTip(TRIM_STATUS_MOUSE_TIP_ID, 'disabled')
  }

  private hideTrimStatusTip(): void {
    this.mouseTipHelper.removeTip(TRIM_STATUS_MOUSE_TIP_ID)
  }

  // ─── 执行修剪 ──────────────────────────────────────────────────────────────

  /**
   * 执行一次修剪：
   * 1. 对光标下方的实体计算修剪结果
   * 2. 通过命令记录（支持撤销/重做）
   * 3. 清除当前预览，等待下一次悬停
   */
  private executeOperate(): void {
    const trimResult = this.trimResultCache
    if (!trimResult || trimResult.deleteEntities.length === 0) return

    // 先清除预览，避免材质引用在删除实体后悬空
    this.restoreHoveredEntity()
    this.clearPreviewObjects()
    this.hideTrimStatusTip()

    // 提交到命令记录，并在视图中执行删除/新增
    this.currentCommand?.addEntity(trimResult.deleteEntities, trimResult.createdEntities)
    this.trimResultCache = undefined
  }
}
